/**
 * «Practicar estas N» of the end-of-game summary (plan 47): fetches the
 * persisted corrections of the game's attempts on tap (`gamePracticeItems.ts`) and
 * opens the coach's correction review over them (`CoachReview`, encargo 99). While the voice session is
 * live it is PAUSED for the practice — the review asks the learner to say each
 * sentence out loud, and the coach would take that as a turn — and resumed on
 * close (unless the learner had paused it already).
 */
import { useState, type ReactElement } from 'react';
import { fetchSessionSummary } from '../../lib/sessionSummaryApi.js';
import type { SessionSummaryDistinctCorrection } from '../../lib/sessionSummaryApi.js';
import { CoachReview, type CoachReviewInput } from './assisted/CoachReview.js';
import type { GameSummaryReview } from './gameArena.js';
import {
  gamePracticeCount,
  gamePracticeItems,
  gamePracticeSessions,
  type GamePracticeHost,
} from './gamePracticeItems.js';

/** Learner-facing copy. */
export const GAME_PRACTICE_COPY = {
  start: (n: number) => (n === 1 ? 'Practicar esta corrección' : `Practicar estas ${n}`),
  loading: 'Preparando…',
  failed: 'No pudimos traer las correcciones. Probá de nuevo.',
  empty: 'No quedan correcciones para practicar.',
  back: 'Volver a la charla',
} as const;

interface GamePracticeProps {
  review: ReadonlyArray<GameSummaryReview>;
  /** The live session's id; without it there is nothing to fetch. */
  sessionId: string | null | undefined;
  host: GamePracticeHost;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'empty' }
  | { kind: 'open'; items: ReadonlyArray<CoachReviewInput>; resumeOnClose: boolean };

export function GamePractice({ review, sessionId, host }: GamePracticeProps): ReactElement | null {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const count = gamePracticeCount(review);
  if (!sessionId || count === 0) return null;

  const start = (): void => {
    if (state.kind === 'loading') return;
    setState({ kind: 'loading' });
    const sessions = gamePracticeSessions(review, sessionId);
    void Promise.all(
      sessions.map(
        async (id): Promise<[string, ReadonlyArray<SessionSummaryDistinctCorrection>]> => {
          const res = await fetchSessionSummary(id);
          const rows =
            res.ready && res.summary
              ? res.summary.common.distinctCorrections
              : (res.partial?.distinctCorrections ?? null);
          if (rows === null) throw new Error(`session ${id}: the summary carried no corrections`);
          return [id, rows];
        },
      ),
    ).then(
      (pairs) => {
        const items = gamePracticeItems(review, new Map(pairs), sessionId);
        if (items.length === 0) {
          setState({ kind: 'empty' });
          return;
        }
        const resumeOnClose = host.live && !host.paused;
        if (resumeOnClose) host.pause();
        setState({ kind: 'open', items, resumeOnClose });
      },
      (err: unknown) => {
        console.error('[game-practice] corrections fetch failed', err);
        setState({ kind: 'failed' });
      },
    );
  };

  const close = (): void => {
    if (state.kind === 'open' && state.resumeOnClose) host.resume();
    setState({ kind: 'idle' });
  };

  return (
    <div className="game-summary__practice">
      <button
        type="button"
        className="game-summary__practice-btn"
        onClick={start}
        disabled={state.kind === 'loading'}
      >
        {state.kind === 'loading' ? GAME_PRACTICE_COPY.loading : GAME_PRACTICE_COPY.start(count)}
      </button>
      {state.kind === 'failed' && (
        <div className="game-summary__practice-note" role="status">
          {GAME_PRACTICE_COPY.failed}
        </div>
      )}
      {state.kind === 'empty' && (
        <div className="game-summary__practice-note" role="status">
          {GAME_PRACTICE_COPY.empty}
        </div>
      )}
      {/* El repaso va en su propio portal a `body`. */}
      {state.kind === 'open' && (
        <CoachReview
          items={state.items}
          source="game"
          finishLabel={GAME_PRACTICE_COPY.back}
          onExit={close}
        />
      )}
    </div>
  );
}
